import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import {
  BuildingStorefrontIcon,
  CreditCardIcon,
  CurrencyDollarIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  DocumentTextIcon,
  ArrowLeftIcon,
  StarIcon,
  MegaphoneIcon,
  PhotoIcon,
  XMarkIcon,
  PlusIcon,
  ChartBarIcon,
  PencilIcon
} from '@heroicons/react/24/outline';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const MerchantBusinessManagement = () => {
  const { businessId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [business, setBusiness] = useState(null);
  const [weeklyCommissions, setWeeklyCommissions] = useState([]);
  const [userSpends, setUserSpends] = useState([]);
  const [uploadingPhotos, setUploadingPhotos] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editFormData, setEditFormData] = useState({
    name: '',
    description: '',
    cuisine: '',
    priceForTwo: '',
    openingHours: {
      Monday: { open: '9:00 AM', close: '10:00 PM', closed: false },
      Tuesday: { open: '9:00 AM', close: '10:00 PM', closed: false },
      Wednesday: { open: '9:00 AM', close: '10:00 PM', closed: false },
      Thursday: { open: '9:00 AM', close: '10:00 PM', closed: false },
      Friday: { open: '9:00 AM', close: '10:00 PM', closed: false },
      Saturday: { open: '9:00 AM', close: '10:00 PM', closed: false },
      Sunday: { open: '9:00 AM', close: '10:00 PM', closed: false }
    },
    phone: '',
    address: '',
    facilities: [],
    mustTryDishes: []
  });
  const [savingEdit, setSavingEdit] = useState(false);

  useEffect(() => {
    if (!user || user.role !== 'merchant') {
      navigate('/login');
      return;
    }
    fetchBusinessData();
  }, [businessId, user, navigate]);

  const fetchBusinessData = async () => {
    try {
      setLoading(true);
      const [businessRes, commissionsRes] = await Promise.all([
        axios.get(`${API_URL}/restaurants/${businessId}`),
        axios.get(`${API_URL}/weekly-commissions/restaurant/${businessId}`).catch(() => ({ data: [] }))
      ]);

      setBusiness(businessRes.data);
      setWeeklyCommissions(commissionsRes.data || []);
    } catch (error) {
      console.error('Error fetching business data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUploadPhotos = async (files) => {
    if (!files || files.length === 0) return;

    try {
      setUploadingPhotos(true);
      const formData = new FormData();
      files.forEach((file) => {
        formData.append('photos', file);
      });

      const response = await axios.post(
        `${API_URL}/restaurants/${businessId}/photos`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      if (response.data.success) {
        // Refresh business data to show new photos
        await fetchBusinessData();
        setSelectedFiles([]);
        alert('Photos uploaded successfully!');
      }
    } catch (error) {
      console.error('Error uploading photos:', error);
      alert(error.response?.data?.message || 'Failed to upload photos. Please try again.');
    } finally {
      setUploadingPhotos(false);
    }
  };

  const handleDeletePhoto = async (photoId) => {
    if (!window.confirm('Are you sure you want to delete this photo?')) {
      return;
    }

    try {
      await axios.delete(`${API_URL}/restaurants/${businessId}/photos/${photoId}`);
      // Refresh business data
      await fetchBusinessData();
      alert('Photo deleted successfully!');
    } catch (error) {
      console.error('Error deleting photo:', error);
      alert(error.response?.data?.message || 'Failed to delete photo. Please try again.');
    }
  };

  const handleOpenEditModal = () => {
    // Handle both old format (simple open/close) and new format (per-day schedule)
    let openingHours;
    if (business.openingHours && business.openingHours.Monday) {
      // New format - per-day schedule
      openingHours = business.openingHours;
    } else if (business.openingHours && business.openingHours.open) {
      // Old format - convert to per-day schedule
      const defaultHours = {
        open: business.openingHours.open,
        close: business.openingHours.close,
        closed: false
      };
      openingHours = {
        Monday: { ...defaultHours },
        Tuesday: { ...defaultHours },
        Wednesday: { ...defaultHours },
        Thursday: { ...defaultHours },
        Friday: { ...defaultHours },
        Saturday: { ...defaultHours },
        Sunday: { ...defaultHours }
      };
    } else {
      // No opening hours - use defaults
      openingHours = {
        Monday: { open: '9:00 AM', close: '10:00 PM', closed: false },
        Tuesday: { open: '9:00 AM', close: '10:00 PM', closed: false },
        Wednesday: { open: '9:00 AM', close: '10:00 PM', closed: false },
        Thursday: { open: '9:00 AM', close: '10:00 PM', closed: false },
        Friday: { open: '9:00 AM', close: '10:00 PM', closed: false },
        Saturday: { open: '9:00 AM', close: '10:00 PM', closed: false },
        Sunday: { open: '9:00 AM', close: '10:00 PM', closed: false }
      };
    }

    setEditFormData({
      name: business.name || '',
      description: business.description || '',
      cuisine: business.cuisine || '',
      priceForTwo: business.priceForTwo || '',
      openingHours,
      phone: business.phone || '',
      address: business.address || '',
      facilities: business.facilities || [],
      mustTryDishes: business.mustTryDishes || []
    });
    setIsEditModalOpen(true);
  };

  const handleEditChange = (e) => {
    const { name, value, type, checked } = e.target;

    // Handle per-day opening hours (format: "Monday-open", "Monday-close", "Monday-closed")
    if (name.includes('-open') || name.includes('-close') || name.includes('-closed')) {
      const [day, field] = name.split('-');
      setEditFormData(prev => ({
        ...prev,
        openingHours: {
          ...prev.openingHours,
          [day]: {
            ...prev.openingHours[day],
            [field]: type === 'checkbox' ? checked : value
          }
        }
      }));
    } else {
      setEditFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleArrayChange = (field, value) => {
    const items = value.split(',').map(item => item.trim()).filter(item => item);
    setEditFormData(prev => ({ ...prev, [field]: items }));
  };

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    try {
      setSavingEdit(true);
      const dataToSend = {
        name: editFormData.name,
        description: editFormData.description,
        cuisine: editFormData.cuisine,
        priceForTwo: Number(editFormData.priceForTwo),
        openingHours: editFormData.openingHours,
        phone: editFormData.phone,
        address: editFormData.address,
        facilities: editFormData.facilities,
        mustTryDishes: editFormData.mustTryDishes
      };

      await axios.put(`${API_URL}/restaurants/${businessId}`, dataToSend);

      setIsEditModalOpen(false);
      await fetchBusinessData();
      alert('Business details updated successfully!');
    } catch (error) {
      console.error('Error updating business:', error);
      alert(error.response?.data?.message || 'Failed to update business details');
    } finally {
      setSavingEdit(false);
    }
  };

  // Subscription feature removed (payment gateway disabled)
  // TODO: Re-enable when payment integration is ready

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!business) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600">Business not found</p>
          <button
            onClick={() => navigate('/merchant/dashboard')}
            className="mt-4 text-primary-600 hover:text-primary-700"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  const totalPendingCommission = weeklyCommissions
    .filter(c => c.status === 'pending' || c.status === 'invoice_generated')
    .reduce((sum, c) => sum + (c.companyCommission || 0), 0);

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={() => navigate('/merchant/dashboard')}
            className="flex items-center text-gray-600 hover:text-gray-900 mb-4"
          >
            <ArrowLeftIcon className="h-5 w-5 mr-2" />
            Back to Dashboard
          </button>
          <div className="flex items-start justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">{business.name}</h1>
              <p className="text-gray-600">{business.address}</p>
            </div>
            <button
              onClick={handleOpenEditModal}
              className="flex items-center gap-2 bg-primary-600 text-white px-4 py-2 rounded-lg font-semibold hover:bg-primary-700"
            >
              <PencilIcon className="h-5 w-5" />
              Edit Business
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200 mb-6">
          <nav className="-mb-px flex space-x-8">
            {['overview', 'photos', 'commissions', 'user-spends'].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`${
                  activeTab === tab
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm capitalize`}
              >
                {tab.replace('-', ' ')}
              </button>
            ))}
          </nav>
        </div>

        {/* Tab Content */}
        {activeTab === 'overview' && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Revenue Model</h3>
                <ChartBarIcon className="h-6 w-6 text-primary-600" />
              </div>
              <p className="text-2xl font-bold text-primary-600 mb-2">15-20%</p>
              <p className="text-sm text-gray-600">
                User discount (LOCU gets 1/3rd)
              </p>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Total Revenue</h3>
                <CurrencyDollarIcon className="h-6 w-6 text-blue-600" />
              </div>
              <p className="text-2xl font-bold text-gray-900">
                ₹{business.totalRevenue?.toLocaleString() || 0}
              </p>
              <p className="text-sm text-gray-500 mt-2">
                {business.totalTransactions || 0} transactions
              </p>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Pending Commissions</h3>
                <ClockIcon className="h-6 w-6 text-yellow-600" />
              </div>
              <p className="text-2xl font-bold text-gray-900">
                ₹{totalPendingCommission.toLocaleString()}
              </p>
              <p className="text-sm text-gray-500 mt-2">
                {weeklyCommissions.filter(c => c.status === 'pending' || c.status === 'invoice_generated').length} weeks
              </p>
            </div>
          </div>
        )}

        {activeTab === 'photos' && (
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-gray-900">Restaurant Photos</h2>
              <label className="bg-primary-600 text-white px-4 py-2 rounded-lg font-semibold hover:bg-primary-700 cursor-pointer flex items-center gap-2">
                <PlusIcon className="h-5 w-5" />
                Upload Photos
                <input
                  type="file"
                  multiple
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => {
                    const files = Array.from(e.target.files);
                    setSelectedFiles(files);
                    handleUploadPhotos(files);
                  }}
                />
              </label>
            </div>

            {uploadingPhotos && (
              <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-blue-800">Uploading photos...</p>
              </div>
            )}

            {business?.photos && business.photos.length > 0 ? (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {business.photos.map((photo, idx) => (
                  <div key={photo._id || idx} className="relative group">
                    <img
                      src={`${API_URL.replace('/api', '')}${photo.url}`}
                      alt={`${business.name} - Photo ${idx + 1}`}
                      className="w-full h-48 object-cover rounded-lg"
                    />
                    <button
                      onClick={() => handleDeletePhoto(photo._id)}
                      className="absolute top-2 right-2 bg-red-600 text-white p-2 rounded-full opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-700"
                    >
                      <XMarkIcon className="h-4 w-4" />
                    </button>
                    {photo.caption && (
                      <p className="mt-2 text-sm text-gray-600 truncate">{photo.caption}</p>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12 border-2 border-dashed border-gray-300 rounded-lg">
                <PhotoIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600 mb-2">No photos uploaded yet</p>
                <p className="text-sm text-gray-500">Upload photos to showcase your restaurant</p>
              </div>
            )}
          </div>
        )}


        {activeTab === 'commissions' && (
          <div className="bg-white rounded-lg shadow">
            <div className="p-6 border-b">
              <h2 className="text-xl font-semibold text-gray-900">Weekly Commissions</h2>
              <p className="text-sm text-gray-600 mt-1">
                Commission is calculated as 15-20% of user spend. 1/3rd goes to LOCU, 2/3rd goes to users as rewards.
              </p>
            </div>
            <div className="p-6">
              {weeklyCommissions.length === 0 ? (
                <div className="text-center py-12">
                  <DocumentTextIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600">No commission records yet</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {weeklyCommissions.map((commission) => (
                    <div key={commission._id} className="border rounded-lg p-4">
                      <div className="flex items-center justify-between mb-4">
                        <div>
                          <h3 className="font-semibold text-gray-900">
                            Week of {new Date(commission.weekStartDate).toLocaleDateString()} - {new Date(commission.weekEndDate).toLocaleDateString()}
                          </h3>
                          {commission.invoiceNumber && (
                            <p className="text-sm text-gray-600">Invoice: {commission.invoiceNumber}</p>
                          )}
                        </div>
                        <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                          commission.status === 'paid' ? 'bg-green-100 text-green-800' :
                          commission.status === 'invoice_generated' ? 'bg-blue-100 text-blue-800' :
                          commission.status === 'overdue' ? 'bg-red-100 text-red-800' :
                          'bg-yellow-100 text-yellow-800'
                        }`}>
                          {commission.status.replace('_', ' ').toUpperCase()}
                        </span>
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div>
                          <p className="text-sm text-gray-600">Total User Spends</p>
                          <p className="text-lg font-semibold">₹{commission.totalUserSpends?.toLocaleString() || 0}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600">Total Commission</p>
                          <p className="text-lg font-semibold">₹{commission.totalCommission?.toLocaleString() || 0}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600">Company Commission (1/3rd)</p>
                          <p className="text-lg font-semibold text-primary-600">
                            ₹{commission.companyCommission?.toLocaleString() || 0}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600">User Rewards (2/3rd)</p>
                          <p className="text-lg font-semibold text-green-600">
                            ₹{commission.userRewardsGiven?.toLocaleString() || 0}
                          </p>
                        </div>
                      </div>
                      {commission.dueDate && (
                        <p className="text-sm text-gray-500 mt-4">
                          Due Date: {new Date(commission.dueDate).toLocaleDateString()}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'user-spends' && (
          <div className="bg-white rounded-lg shadow">
            <div className="p-6 border-b">
              <h2 className="text-xl font-semibold text-gray-900">User Spends</h2>
              <p className="text-sm text-gray-600 mt-1">
                Track all user spends at your restaurant (via UPI payments or bill OCR)
              </p>
            </div>
            <div className="p-6">
              {userSpends.length === 0 ? (
                <div className="text-center py-12">
                  <DocumentTextIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600">No user spends recorded yet</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {userSpends.map((spend) => (
                    <div key={spend._id} className="border rounded-lg p-4">
                      <div className="flex items-center justify-between mb-2">
                        <div>
                          <p className="font-semibold text-gray-900">₹{spend.amount?.toLocaleString()}</p>
                          <p className="text-sm text-gray-600">
                            {spend.verificationMethod === 'upi_payment' ? 'UPI Payment' : 'Bill OCR'}
                          </p>
                        </div>
                        <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                          spend.status === 'verified' ? 'bg-green-100 text-green-800' :
                          spend.status === 'points_allocated' ? 'bg-blue-100 text-blue-800' :
                          'bg-yellow-100 text-yellow-800'
                        }`}>
                          {spend.status.replace('_', ' ').toUpperCase()}
                        </span>
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
                        <div>
                          <p className="text-sm text-gray-600">Commission</p>
                          <p className="text-sm font-semibold">₹{spend.totalCommission?.toFixed(2) || 0}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600">Points Allocated</p>
                          <p className="text-sm font-semibold text-green-600">
                            ₹{spend.pointsAllocated?.toFixed(2) || 0}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600">Company Commission</p>
                          <p className="text-sm font-semibold text-primary-600">
                            ₹{spend.companyCommission?.toFixed(2) || 0}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600">Date</p>
                          <p className="text-sm font-semibold">
                            {new Date(spend.spendDate).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Edit Business Modal */}
        {isEditModalOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b sticky top-0 bg-white">
                <div className="flex items-center justify-between">
                  <h2 className="text-2xl font-bold text-gray-900">Edit Business Details</h2>
                  <button
                    onClick={() => setIsEditModalOpen(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>
              </div>

              <form onSubmit={handleSaveEdit} className="p-6 space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Restaurant Name *
                    </label>
                    <input
                      type="text"
                      name="name"
                      required
                      value={editFormData.name}
                      onChange={handleEditChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Cuisine Type
                    </label>
                    <input
                      type="text"
                      name="cuisine"
                      value={editFormData.cuisine}
                      onChange={handleEditChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                      placeholder="e.g., Italian, Indian, Chinese"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Phone Number *
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      required
                      value={editFormData.phone}
                      onChange={handleEditChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Price for Two
                    </label>
                    <input
                      type="number"
                      name="priceForTwo"
                      value={editFormData.priceForTwo}
                      onChange={handleEditChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                      placeholder="₹"
                    />
                  </div>

                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      Opening Hours (Per Day)
                    </label>
                    <div className="space-y-3 bg-gray-50 p-4 rounded-md">
                      {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map((day) => (
                        <div key={day} className="flex items-center gap-3 bg-white p-3 rounded border border-gray-200">
                          <div className="w-24 flex items-center gap-2">
                            <input
                              type="checkbox"
                              name={`${day}-closed`}
                              checked={editFormData.openingHours[day]?.closed || false}
                              onChange={handleEditChange}
                              className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                            />
                            <label className="text-sm font-medium text-gray-700">{day}</label>
                          </div>
                          {editFormData.openingHours[day]?.closed ? (
                            <span className="text-sm text-gray-500 italic">Closed</span>
                          ) : (
                            <>
                              <input
                                type="text"
                                name={`${day}-open`}
                                value={editFormData.openingHours[day]?.open || ''}
                                onChange={handleEditChange}
                                className="flex-1 px-3 py-1.5 border border-gray-300 rounded text-sm focus:ring-primary-500 focus:border-primary-500"
                                placeholder="9:00 AM"
                              />
                              <span className="text-gray-500">to</span>
                              <input
                                type="text"
                                name={`${day}-close`}
                                value={editFormData.openingHours[day]?.close || ''}
                                onChange={handleEditChange}
                                className="flex-1 px-3 py-1.5 border border-gray-300 rounded text-sm focus:ring-primary-500 focus:border-primary-500"
                                placeholder="10:00 PM"
                              />
                            </>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Address *
                  </label>
                  <textarea
                    name="address"
                    required
                    value={editFormData.address}
                    onChange={handleEditChange}
                    rows="2"
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description
                  </label>
                  <textarea
                    name="description"
                    value={editFormData.description}
                    onChange={handleEditChange}
                    rows="4"
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                    placeholder="Tell customers about your restaurant..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Facilities (comma-separated)
                  </label>
                  <input
                    type="text"
                    value={editFormData.facilities.join(', ')}
                    onChange={(e) => handleArrayChange('facilities', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                    placeholder="e.g., Wi-Fi, Parking, Outdoor Seating"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Must-Try Dishes (comma-separated)
                  </label>
                  <input
                    type="text"
                    value={editFormData.mustTryDishes.join(', ')}
                    onChange={(e) => handleArrayChange('mustTryDishes', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                    placeholder="e.g., Margherita Pizza, Pasta Alfredo"
                  />
                </div>

                <div className="flex gap-4 pt-4">
                  <button
                    type="button"
                    onClick={() => setIsEditModalOpen(false)}
                    className="flex-1 px-6 py-3 border border-gray-300 rounded-lg font-semibold text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={savingEdit}
                    className="flex-1 px-6 py-3 bg-primary-600 text-white rounded-lg font-semibold hover:bg-primary-700 disabled:opacity-50"
                  >
                    {savingEdit ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MerchantBusinessManagement;
